import Service, { inject as service } from '@ember/service';
import ApiService, { RequestOptions } from 'cio/services/api';
import moment from 'moment-timezone';
import { Entities, MetricTypes, PERIOD_KEYS, Periods, timeRangeToStepsParams } from 'cio/utils/metrics/common';
import { Metric } from 'cio/utils/metrics/models/metric';
import { SplitMetric } from 'cio/utils/metrics/models/split-metric';
import { TrackedLinkMetric } from 'cio/utils/metrics/models/tracked-link-metric';
import {
  deserialize as deserializeMetricResponse,
  deserializeCampaignActionMetrics as deserializeCampaignActionMetricsResponse,
  deserializeCampaignChannelMetrics as deserializeCampaignChannelMetricsResponse,
} from 'cio/utils/metrics/deserialize/metric';
import {
  deserialize as deserializeSplitMetricResponse,
  deserializeCampaignSplitMetrics as deserializeCampaignSplitMetricsResponse,
} from 'cio/utils/metrics/deserialize/split-metric';
import { deserialize as deserializeTemplateMetricResponse } from 'cio/utils/metrics/deserialize/template-metric';
import { deserialize as deserializeTrackedLinkMetricResponse } from 'cio/utils/metrics/deserialize/tracked-link-metric';
import { deserialize as deserializeTrackedResponseMetricResponse } from 'cio/utils/metrics/deserialize/tracked-response-metric';
import { deserialize as deserializeSegmentMembershipMetricResponse } from 'cio/utils/metrics/deserialize/segment-membership-metric';
import { deserialize as deserializeJourneyMetricResponse } from 'cio/utils/metrics/deserialize/journey-metric';
import { deserialize as deserializeDashboardTestsResponse } from 'cio/utils/metrics/deserialize/dashboard-tests';
import { pluralize } from 'ember-inflector';
import { CampaignState, CampaignType, TYPE_ICONS } from 'cio/models/campaign';
import { TimeRange, unixNowMilliseconds } from 'cio/utils/time';
import { DELIVERY_METRICS_MEASURES, JOURNEY_METRICS_MEASURES } from 'cio/utils/metrics/analysis';
import merge from 'lodash.merge';
import { isNone } from '@ember/utils';
import { MIN_START_TIME_UTC } from 'cio/utils/journey-metrics';
import { ActionType } from 'cio/models/action';
import { deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import EnvironmentService from './environment';
import { Grouping } from 'cio/utils/metrics/resolution';
import { DashboardTestResult } from 'cio/utils/metrics/models/dashboard-tests';
import { measureDenominators, MeasureType } from 'cio/utils/metrics/measure';
import DS from 'ember-data';
import SettingsService from './settings';
import SubscriptionTopicModel from 'cio/models/subscription-topic';
import WorkersService, { CIOWorker } from './workers';

const RECENT_REQUEST_THRESHOLD_IN_SECONDS = 30;

const DEFAULT_ANALYSIS_EXTRA_OPTIONS = {
  excludeZeros: false,
  excludeNewslettersWithoutSends: false,
  refreshData: false,
};

class MetricRequest {
  uuid: string;

  id: string;

  metricType: MetricTypes;
  entityType: Entities;
  entityId: string;

  // "period" is an argument for the API that is mainly used in older-style
  // metrics. It can be:
  // - "custom" - the date range is specified by the UI in the call through
  // other params
  // - "days" - the API returns the "last 30 days" of data (in reality it's 45
  // entries, because in the past we used to calculate 2 weeks of rolling
  // averages)
  // - "weeks" - last 12 weeks of data
  // - "months" - last 12 months of data
  // We are slowly moving away from this setup and in future all metrics
  // requests should be based on the time range provided
  period: Periods | null;

  // "resolution" tells us what buckets the data is grouped into coming from the
  // API (days, weeks, months)
  resolution: Periods | null;

  actionType: string | null;
  range: TimeRange | null;
  request: Promise<any>;

  result?: null | Metric | Metric[] | SplitMetric[] | TrackedLinkMetric | DashboardTestResult;

  fetchedAt?: number;

  environmentId?: string;
  campaignId?: string;
  newsletterId?: string;

  constructor(
    metricType: MetricTypes,
    entityType: Entities,
    entityId: number | string,
    period: Periods | null,
    resolution: Periods | null,
    range: TimeRange | null,
    actionType: string | null,
    request: any,
    meta?: {
      environmentId?: number | string;
      campaignId?: number | string;
      newsletterId?: number | string;
      ignoreRangeIfNotCustom?: boolean;
    },
  ) {
    this.uuid = window.UUID.generate();

    this.metricType = metricType;
    this.entityType = entityType;
    this.entityId = String(entityId);

    this.period = period;
    if (!resolution) {
      if (period === Periods.CUSTOM || period === Periods.DAYS) {
        this.resolution = Periods.DAYS;
      } else {
        this.resolution = period;
      }
    } else {
      this.resolution = resolution;
    }

    this.range = range;
    this.actionType = actionType;

    this.id = generateMetricIdentifier(
      this.metricType,
      this.entityType,
      this.entityId,
      this.period,
      this.resolution,
      this.range,
      this.actionType,
    );

    this.request = request;

    if (meta?.environmentId != null) {
      this.environmentId = String(meta.environmentId);
    }

    if (meta?.campaignId != null) {
      this.campaignId = String(meta.campaignId);
    }

    if (meta?.newsletterId != null) {
      this.newsletterId = String(meta.newsletterId);
    }
  }

  async runRequest() {
    let result = await this.request;
    this.result = result;
    this.fetchedAt = Math.floor(Date.now() / 1000);
    return result;
  }
}

function generateMetricIdentifier(
  metricType: MetricTypes,
  entityType: Entities,
  entityId: number | string,
  period: Periods | null,
  resolution: Periods | null,
  range: TimeRange | null,
  actionType: string | null,
) {
  return `${metricType}_${entityType}_${entityId}_${actionType}_${period}_${resolution}_${range?.start?.unix()}_${range?.end?.unix()}`;
}

export function buildRequestPath(entityType: Entities, entityId: number | string, period: Periods) {
  let path = '';

  if (entityType === Entities.ENVIRONMENT) {
    path = 'metrics';

    if (period === Periods.ALL_TIME) {
      path = 'all_metrics';
    }
  } else {
    path = `${pluralize(entityType)}/${entityId}/metrics`;
  }

  return path;
}

export function buildTemplateMetricsRequestPath(newsletterId: number | string) {
  return `newsletters/${newsletterId}/template_metrics`;
}

export function buildTrackedLinkMetricRequestPath(
  entityType: Entities,
  entityId: number | string,
  actionType?: string,
) {
  let path: string;

  if (entityType === Entities.ENVIRONMENT) {
    if (actionType === 'all') {
      path = 'metrics/all_links';
    } else {
      path = 'tracked_link_metric';
    }
  } else {
    path = `${pluralize(entityType)}/${entityId}/tracked_link_metric`;
  }

  return path;
}

export function buildTrackedResponseMetricsRequestPath(entityType: Entities, entityId: number | string) {
  return `${pluralize(entityType)}/${entityId}/tracked_response_metrics`;
}

export function buildSplitMetricRequestPath(entityId: number | string) {
  return `${pluralize(Entities.ACTION)}/${entityId}/split_metrics`;
}

export function buildV2RequestOptions(resolution: Periods | null, range: TimeRange | null, timezone: string) {
  let res = resolution;

  if (res === 'week') {
    res = Periods.DAYS;
  }

  let options: RequestOptions = {
    data: {
      res,
      version: 2,
      start:
        range?.start != null ? moment.tz(`${range?.start?.format('YYYY-MM-DD')} 00:00:00`, timezone).unix() : undefined,
      end: range?.end != null ? moment.tz(`${range?.end?.format('YYYY-MM-DD')} 00:00:00`, timezone).unix() : undefined,
      tz: timezone,
    },
  };

  return options;
}

export function buildRequestOptions(
  metricType: MetricTypes,
  entityType: Entities,
  entityId: number | string,
  period: Periods | null,
  resolution: Periods | null,
  range: TimeRange | null,
  actionType?: string | null,
  timezone?: string,
) {
  let options: RequestOptions = { data: {} };

  if (entityType === Entities.ENVIRONMENT) {
    options.replaceEnvironmentID = `${entityId}`;
  }

  if (period !== Periods.ALL_TIME) {
    options.data.period = period;
  }

  if (entityType !== Entities.ACTION && actionType) {
    options.data.type = actionType;
  }

  if (period === Periods.CUSTOM && range) {
    let params = timeRangeToStepsParams(range, timezone ?? 'Etc/GMT+4');
    options.data.start = params?.start;
    options.data.steps = params?.steps;
  }

  if (metricType === MetricTypes.SplitMetric || metricType === MetricTypes.TemplateMetric) {
    delete options.data.period;
  }

  if (metricType === MetricTypes.SegmentMembershipMetric) {
    options.data.resolution = resolution;
    options.data.segment_id = entityId;
    delete options.data.period;

    if (range?.start && range?.end) {
      options.data.start = moment(range.start).utc().unix();
      options.data.end = moment(range.end).utc().unix();
    }
  }

  if (metricType === MetricTypes.TrackedLinkMetric && entityType === Entities.ENVIRONMENT && actionType === 'all') {
    delete options.data.period;
    delete options.data.type;

    options.data.resolution = period === Periods.CUSTOM ? Periods.DAYS : period;
    options.data.start = range?.start?.unix();
    options.data.end = range?.end?.unix();
  }

  if (metricType === MetricTypes.TrackedLinkMetric && entityType === Entities.NEWSLETTER) {
    delete options.data.type;
  }

  return options;
}

type AnalysisAPIResponse = {
  metrics: AnalysisAPIResponseMetric[];
};

type AnalysisAPIResponseMetric =
  | {
      campaign_id: number;
      type: CampaignType;
      name: string;
      data: {
        delivery_metrics: Record<string, Record<string, number[]>>;
        journey_metrics: Record<string, number[]>;
        subscription_topic_metrics: Record<string, Record<string, number[]>>;
      };
    }
  | {
      newsletter_id: number;
      type: 'newsletter';
      name: string;
      data: {
        newsletter_metrics: Record<string, number[]>;
      };
    }
  | {
      transactional_id: number;
      type: 'transactional_message';
      name: string;
      is_default?: boolean;
      data: {
        transactional_metrics: Record<string, number[]>;
      };
    };

export default class MetricsService extends Service {
  @service api!: ApiService;
  @service settings!: SettingsService;
  @service store!: DS.Store;
  @service environment!: EnvironmentService;
  @service workers!: WorkersService;

  _requestStore: MetricRequest[] = [];

  get metricsTz() {
    return this.settings.environmentSettings.metricsTimezone;
  }

  clearMetrics(metricType: MetricTypes | null, entityType: Entities, entityId?: number | string) {
    let id = entityId != null ? String(entityId) : null;

    this._requestStore = this._requestStore.filter((r) => {
      let shouldKeep = (metricType != null && r.metricType !== metricType) || r.entityType !== entityType;

      if (id != null && !shouldKeep) {
        shouldKeep = r.entityId !== id;
      }

      if (entityType === Entities.ENVIRONMENT) {
        return r.environmentId !== id && shouldKeep;
      }

      if (entityType === Entities.CAMPAIGN) {
        return r.campaignId !== id && shouldKeep;
      }

      if (entityType === Entities.NEWSLETTER) {
        return r.newsletterId !== id && shouldKeep;
      }

      return shouldKeep;
    });
  }

  clearAllMetrics() {
    this._requestStore = [];
  }

  async requestAndSerialize(
    metricType: MetricTypes,
    request: any,
    entity: Entities,
    entityId: number | string,
    period: Periods | null,
    resolution: Periods | null,
    range: TimeRange | null,
    apiRange: TimeRange | null,
    actionType: string | null,
    timezone?: string,
    meta?: {
      environmentId?: number | string;
      campaignId?: number | string;
      newsletterId?: number | string;
      canConvert?: boolean;
      version?: number;
    },
  ) {
    let result = await request;

    let periodIsCustomOrOneWeek = period != null && [Periods.CUSTOM, Periods.WEEK].includes(period);

    if (metricType === MetricTypes.Metric) {
      return deserializeMetricResponse(
        result,
        entity,
        entityId,
        period as any,
        periodIsCustomOrOneWeek ? Grouping.Daily : (period as any),
        periodIsCustomOrOneWeek ? Periods.DAYS : (period as any),
        range,
        apiRange,
        actionType,
        meta,
      );
    }

    if (metricType === MetricTypes.CampaignActionMetrics) {
      return deserializeCampaignActionMetricsResponse(
        result,
        entityId,
        period as any,
        period === Periods.CUSTOM ? Grouping.Daily : (period as any),
        period === Periods.CUSTOM ? Periods.DAYS : (period as any),
        range,
        apiRange,
        meta ?? null,
        this.store,
      );
    }

    if (metricType === MetricTypes.CampaignChannelMetrics) {
      return deserializeCampaignChannelMetricsResponse(
        result,
        entityId,
        period as any,
        period === Periods.CUSTOM ? Grouping.Daily : (period as any),
        period === Periods.CUSTOM ? Periods.DAYS : (period as any),
        range,
        apiRange,
        meta,
      );
    }

    if (metricType === MetricTypes.JourneyMetric) {
      return deserializeJourneyMetricResponse(result, resolution, Grouping.Daily, range as TimeRange, timezone);
    }

    if (metricType === MetricTypes.SplitMetric && entity === Entities.CAMPAIGN) {
      return deserializeCampaignSplitMetricsResponse(result, entityId, meta);
    }

    if (metricType === MetricTypes.SplitMetric) {
      return deserializeSplitMetricResponse(result, entityId, meta);
    }

    if (metricType === MetricTypes.TemplateMetric) {
      return deserializeTemplateMetricResponse(result, meta);
    }

    if (metricType === MetricTypes.TrackedLinkMetric) {
      return deserializeTrackedLinkMetricResponse(
        result,
        entity,
        entityId,
        period,
        resolution,
        range,
        actionType,
        meta,
      );
    }

    if (metricType === MetricTypes.TrackedResponseMetric) {
      return deserializeTrackedResponseMetricResponse(result, entity, entityId, resolution, range);
    }

    if (metricType === MetricTypes.SegmentMembershipMetric) {
      return deserializeSegmentMembershipMetricResponse(result, entityId, range, meta);
    }

    if (metricType === MetricTypes.DashboardTests) {
      return deserializeDashboardTestsResponse(result, range as TimeRange, meta);
    }

    return null;
  }

  private maybeGetRunningOrQueuedRequest(
    metricType: MetricTypes,
    entityType: Entities,
    entityId: number | string,
    period: Periods | null,
    resolution: Periods | null,
    range: TimeRange | null,
    actionType: string | null,
  ): MetricRequest | null {
    let identifier = generateMetricIdentifier(metricType, entityType, entityId, period, resolution, range, actionType);

    let existingRequest = this._requestStore.find((r) => r.id === identifier);

    // If no existing request has been found, return nothing early
    if (!existingRequest) {
      return null;
    }

    // If a request for this metric already exists but hasn't finished running
    // yet, return the same promise to avoid sending duplicate API requests.
    if (!existingRequest.result) {
      return existingRequest;
    }

    // If the request for the same metric was performed in the past 30
    // seconds, return the same instance to reduce spamming the API.
    let now = Math.floor(Date.now() / 1000);
    if (existingRequest.fetchedAt && existingRequest.fetchedAt > now - RECENT_REQUEST_THRESHOLD_IN_SECONDS) {
      return existingRequest;
    }

    // If the existing request has a result and is older than 30 seconds,
    // remove it from the store. Return null to let the caller know nothing
    // relevant was found and let it  continue to create a new request.
    this._requestStore = this._requestStore.filter((r) => r.uuid !== existingRequest?.uuid);
    return null;
  }

  async fetchMetric(
    entityType: Entities,
    entityId: number | string,
    period: Periods,
    range: TimeRange | null,
    actionType?: string | null,
    meta?: {
      environmentId?: number | string;
      campaignId?: number | string;
      newsletterId?: number | string;
      canConvert?: boolean;
      version?: number;
    },
  ) {
    if (!Object.values(Entities).includes(entityType)) {
      return null;
    }

    let useV2 = meta?.version === 2;

    let _range: TimeRange | null = {
      start:
        range?.start != null ? moment.tz(`${range?.start?.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz) : undefined,
      end: range?.end != null ? moment.tz(`${range?.end?.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz) : undefined,
    };

    let apiRange: TimeRange | null = null;

    let resolution = period;

    if (period === Periods.CUSTOM) {
      resolution = Periods.DAYS;
    }

    if (useV2) {
      if (_range?.start && _range?.end) {
        apiRange = {
          start: moment(_range.start),
          end: moment(_range.end),
        };
      } else {
        _range = null;
      }
    } else {
      if (period === Periods.CUSTOM && _range?.start && _range?.end) {
        apiRange = {
          start: moment(_range.start),
          end: moment(_range.end),
        };
      } else {
        _range = null;
      }
    }

    let _meta = meta != null ? { ...meta } : {};

    if (meta?.environmentId != null) {
      _meta.environmentId = Number(meta.environmentId);
    }
    if (meta?.campaignId != null) {
      _meta.campaignId = Number(meta.campaignId);
    }
    if (meta?.newsletterId != null) {
      _meta.newsletterId = Number(meta.newsletterId);
    }

    if (meta?.canConvert == null && entityType === Entities.ACTION) {
      let actionModel = this.store.peekRecord('action', entityId);
      if (actionModel != null) {
        _meta.canConvert = actionModel.canConvert;
      }
    }

    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.Metric,
      entityType,
      entityId,
      period,
      resolution,
      range,
      actionType ?? null,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let path = buildRequestPath(entityType, entityId, period);
    let options = useV2
      ? buildV2RequestOptions(resolution, apiRange, this.metricsTz)
      : buildRequestOptions(MetricTypes.Metric, entityType, entityId, period, period, apiRange, actionType);

    let apiRequest = this.api.request(path, options);
    let request = this.requestAndSerialize(
      MetricTypes.Metric,
      apiRequest,
      entityType,
      entityId,
      period,
      resolution,
      _range,
      apiRange,
      actionType ?? null,
      this.settings.environmentSettings.metricsTimezone,
      _meta,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.Metric,
      entityType,
      entityId,
      period,
      resolution,
      _range,
      actionType ?? null,
      request,
      _meta,
    );

    this._requestStore = [...this._requestStore, metricRequest];
    return await metricRequest.runRequest();
  }

  async fetchCampaignChannelDeliveryMetrics(campaignId: number | string, period: Periods, range: TimeRange) {
    let _range: TimeRange | null = {
      start: moment.tz(`${range.start?.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
      end: moment.tz(`${range.end?.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
    };

    let apiRange: TimeRange | null = null;

    let resolution = period;

    if (period === Periods.CUSTOM) {
      resolution = Periods.DAYS;
    }

    apiRange = {
      start: moment(_range.start),
      end: moment(_range.end),
    };

    let meta = {
      campaignId,
      environmentId: this.environment.current.id,
      version: 2,
    };

    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.CampaignChannelMetrics,
      Entities.CAMPAIGN,
      campaignId,
      period,
      resolution,
      _range,
      null,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let path = `campaigns/${campaignId}/channel_metrics`;
    let options = buildV2RequestOptions(resolution, apiRange, this.metricsTz);

    let apiRequest = this.api.request(path, options);
    let request = this.requestAndSerialize(
      MetricTypes.CampaignChannelMetrics,
      apiRequest,
      Entities.CAMPAIGN,
      campaignId,
      period,
      resolution,
      _range,
      apiRange,
      null,
      this.settings.environmentSettings.metricsTimezone,
      meta,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.CampaignChannelMetrics,
      Entities.CAMPAIGN,
      campaignId,
      period,
      resolution,
      _range,
      null,
      request,
      meta,
    );

    this._requestStore = [...this._requestStore, metricRequest];
    return await metricRequest.runRequest();
  }

  async fetchCampaignActionDeliveryMetrics(
    campaignId: number | string,
    period: Periods,
    range: TimeRange,
    meta: {
      environmentId?: number | string;
      campaignId?: number | string;
      canConvert?: boolean;
      version?: number;
    } = {},
  ) {
    let _range: TimeRange | null = {
      start: moment.tz(`${range.start!.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
      end: moment.tz(`${range.end!.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
    };

    let resolution = period;

    if (period === Periods.CUSTOM) {
      resolution = Periods.DAYS;
    }

    let _meta: Record<string, any> = { ...meta };

    _meta.environmentId = this.environment.current.id;
    _meta.campaignId = campaignId;
    _meta.version = 2;

    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.CampaignActionMetrics,
      Entities.CAMPAIGN,
      campaignId,
      period,
      resolution,
      _range,
      null,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let path = `campaigns/${campaignId}/action_metrics`;
    let options = buildV2RequestOptions(resolution, _range, this.metricsTz);

    let apiRequest = this.api.request(path, options);
    let request = this.requestAndSerialize(
      MetricTypes.CampaignActionMetrics,
      apiRequest,
      Entities.CAMPAIGN,
      campaignId,
      period,
      resolution,
      _range,
      _range,
      null,
      this.settings.environmentSettings.metricsTimezone,
      _meta,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.CampaignActionMetrics,
      Entities.CAMPAIGN,
      campaignId,
      period,
      resolution,
      _range,
      null,
      request,
      _meta,
    );

    this._requestStore = [...this._requestStore, metricRequest];
    return await metricRequest.runRequest();
  }

  async fetchEnvironmentDeliveryMetrics(entityId: number | string, period: Periods, range: TimeRange) {
    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.Metric,
      Entities.ENVIRONMENT,
      entityId,
      period,
      period,
      range,
      null,
    );

    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let _range = {
      start: moment.tz(`${range.start!.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
      end: moment.tz(`${range.end!.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
    };

    let path = 'metrics/all_deliveries';
    let options = {
      data: {
        res: period === Periods.CUSTOM ? Periods.DAYS : period,
        start: _range.start.unix(),
        end: _range.end?.unix(),
        tz: this.settings.environmentSettings.metricsTimezone,
        version: 2,
      },
    };

    let apiRequest = this.api.request(path, options);

    let metricRequest = new MetricRequest(
      MetricTypes.Metric,
      Entities.ENVIRONMENT,
      entityId,
      period,
      period,
      _range,
      null,
      apiRequest,
    );

    this._requestStore = [...this._requestStore, metricRequest];

    return await metricRequest.runRequest();
  }

  async fetchJourneyMetrics(entityId: number | string, resolution: Periods, range: TimeRange) {
    if (range.start == null || range.end == null) {
      throw new Error('Time range must have both start and end timestamps');
    }

    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.JourneyMetric,
      Entities.CAMPAIGN,
      entityId,
      null,
      resolution,
      range,
      null,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let _range = {
      start: moment.tz(`${range.start.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
      end: moment.tz(`${range.end.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
    };

    if (_range.start.unix() < MIN_START_TIME_UTC) {
      _range.start = moment.unix(MIN_START_TIME_UTC);
    }

    if (_range.end.unix() < MIN_START_TIME_UTC) {
      _range.end = moment.unix(MIN_START_TIME_UTC).add(range.end.diff(range.start, 'days'), 'days');
    }

    let path = `campaigns/${entityId}/journey_metrics`;
    let options = {
      data: {
        res: PERIOD_KEYS[Periods.DAYS],
        start: _range.start.unix(),
        end: _range.end.unix(),
        standardized_params: true,
        tz: this.metricsTz,
        version: 2,
      },
    };
    let apiRequest = this.api.request(path, options);

    let request = this.requestAndSerialize(
      MetricTypes.JourneyMetric,
      apiRequest,
      Entities.CAMPAIGN,
      entityId,
      null,
      resolution,
      _range,
      _range,
      null,
      this.settings.environmentSettings.journeyMetricsTimezone,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.JourneyMetric,
      Entities.CAMPAIGN,
      entityId,
      null,
      resolution,
      _range,
      null,
      request,
    );

    this._requestStore = [...this._requestStore, metricRequest];

    return await metricRequest.runRequest();
  }

  async fetchCampaignSplitMetrics(campaignId: number | string, meta?: { environmentId?: number | string }) {
    campaignId = Number(campaignId);

    let _meta: Record<string, any> = meta != null ? { ...meta } : {};

    if (meta) {
      if (meta.environmentId) {
        _meta.environmentId = Number(meta.environmentId);
      }
    }

    _meta.campaignId = campaignId;

    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.SplitMetric,
      Entities.CAMPAIGN,
      campaignId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      null,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let path = `campaigns/${campaignId}/split_metrics`;
    let options = buildRequestOptions(
      MetricTypes.SplitMetric,
      Entities.CAMPAIGN,
      campaignId,
      Periods.DAYS,
      Periods.DAYS,
      null,
    );

    let apiRequest = this.api.request(path, options);
    let request = this.requestAndSerialize(
      MetricTypes.SplitMetric,
      apiRequest,
      Entities.CAMPAIGN,
      campaignId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      null,
      null,
      this.settings.environmentSettings.metricsTimezone,
      _meta,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.SplitMetric,
      Entities.CAMPAIGN,
      campaignId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      null,
      request,
      _meta,
    );

    this._requestStore = [...this._requestStore, metricRequest];

    return await metricRequest.runRequest();
  }

  async fetchSplitMetric(
    entityId: number | string,
    meta?: { environmentId?: number | string; campaignId?: number | string },
  ) {
    entityId = Number(entityId);

    if (meta) {
      if (meta.environmentId) {
        meta.environmentId = Number(meta.environmentId);
      }
      if (meta.campaignId) {
        meta.campaignId = Number(meta.campaignId);
      }
    }

    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.SplitMetric,
      Entities.ACTION,
      entityId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      null,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let path = buildSplitMetricRequestPath(entityId);
    let options = buildRequestOptions(
      MetricTypes.SplitMetric,
      Entities.ACTION,
      entityId,
      Periods.DAYS,
      Periods.DAYS,
      null,
    );

    let apiRequest = this.api.request(path, options);
    let request = this.requestAndSerialize(
      MetricTypes.SplitMetric,
      apiRequest,
      Entities.ACTION,
      entityId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      null,
      null,
      this.settings.environmentSettings.metricsTimezone,
      meta,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.SplitMetric,
      Entities.ACTION,
      entityId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      null,
      request,
      meta,
    );

    this._requestStore = [...this._requestStore, metricRequest];

    return await metricRequest.runRequest();
  }

  async fetchTemplateMetrics(newsletterId: number | string, actionType: ActionType) {
    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.TemplateMetric,
      Entities.NEWSLETTER,
      newsletterId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      actionType,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let meta = {
      newsletterId,
    };

    let path = buildTemplateMetricsRequestPath(newsletterId);
    let options = buildRequestOptions(
      MetricTypes.TemplateMetric,
      Entities.NEWSLETTER,
      newsletterId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      actionType,
    );

    let apiRequest = this.api.request(path, options);
    let request = this.requestAndSerialize(
      MetricTypes.TemplateMetric,
      apiRequest,
      Entities.NEWSLETTER,
      newsletterId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      null,
      actionType,
      this.settings.environmentSettings.metricsTimezone,
      meta,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.TemplateMetric,
      Entities.NEWSLETTER,
      newsletterId,
      Periods.DAYS,
      Periods.DAYS,
      null,
      actionType,
      request,
      meta,
    );

    this._requestStore = [...this._requestStore, metricRequest];

    return await metricRequest.runRequest();
  }

  async fetchTrackedLinkMetric(
    entityType: Entities,
    entityId: number | string,
    period: Periods,
    range: TimeRange | null,
    actionType?: string,
    meta?: {
      environmentId?: number | string;
      campaignId?: number | string;
      newsletterId?: number | string;
      transactionalMessageId?: number | string;
      version?: number;
    },
  ) {
    if (!Object.values(Entities).includes(entityType)) {
      return null;
    }

    if (entityType !== Entities.ENVIRONMENT && !isNone(actionType) && period !== Periods.CUSTOM) {
      range = null;
    }

    if (entityType === Entities.TEMPLATE) {
      let template = this.store.peekRecord(entityType, entityId as string);
      if (template?.newsletter !== null) {
        let sentAt = template?.newsletter.sentAt ?? 0;
        range = {
          start: moment.unix(sentAt),
          end: moment.unix(sentAt).clone().add(4, 'months'),
        };
        period = Periods.MONTHS;
      }
    }

    if (meta) {
      if (meta.environmentId) {
        meta.environmentId = Number(meta.environmentId);
      }
      if (meta.campaignId) {
        meta.campaignId = Number(meta.campaignId);
      }
      if (meta.newsletterId) {
        meta.newsletterId = Number(meta.newsletterId);
      }
      if (meta.transactionalMessageId) {
        meta.transactionalMessageId = Number(meta.transactionalMessageId);
      }
    }

    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.TrackedLinkMetric,
      entityType,
      entityId,
      period,
      period,
      range,
      actionType ?? null,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let path = buildTrackedLinkMetricRequestPath(entityType, entityId, actionType);
    let options: RequestOptions = buildV2RequestOptions(
      period === Periods.CUSTOM ? Periods.DAYS : period,
      range,
      this.settings.environmentSettings.metricsTimezone,
    );

    let apiRequest = this.api.request(path, options);
    let request = this.requestAndSerialize(
      MetricTypes.TrackedLinkMetric,
      apiRequest,
      entityType,
      entityId,
      period,
      period,
      range,
      null,
      actionType ?? null,
      this.settings.environmentSettings.metricsTimezone,
      meta,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.TrackedLinkMetric,
      entityType,
      entityId,
      period,
      period,
      range,
      actionType ?? null,
      request,
      meta,
    );

    this._requestStore = [...this._requestStore, metricRequest];

    return await metricRequest.runRequest();
  }

  async fetchTrackedResponseMetric(
    entityType: Entities,
    entityId: number | string,
    resolution: Periods,
    range: TimeRange | null,
  ) {
    if (!Object.values(Entities).includes(entityType)) {
      return null;
    }

    let validEntities = [Entities.CAMPAIGN, Entities.NEWSLETTER];
    if (!validEntities.includes(entityType)) {
      return null;
    }

    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.TrackedResponseMetric,
      entityType,
      entityId,
      null,
      resolution,
      range,
      null,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let path = buildTrackedResponseMetricsRequestPath(entityType, entityId);
    let options: RequestOptions = buildV2RequestOptions(
      resolution,
      range,
      this.settings.environmentSettings.metricsTimezone,
    );

    let apiRequest = this.api.request(path, options);
    let request = this.requestAndSerialize(
      MetricTypes.TrackedResponseMetric,
      apiRequest,
      entityType,
      entityId,
      null,
      resolution,
      range,
      null,
      null,
      this.settings.environmentSettings.metricsTimezone,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.TrackedResponseMetric,
      entityType,
      entityId,
      null,
      resolution,
      range,
      null,
      request,
    );

    this._requestStore = [...this._requestStore, metricRequest];

    return await metricRequest.runRequest();
  }

  async fetchSegmentMembershipMetrics(
    segmentId: number | string,
    resolution: Periods,
    range: TimeRange | null,
    meta?: { environmentId?: number | string },
  ) {
    if (meta?.environmentId) {
      meta.environmentId = Number(meta.environmentId);
    }

    if (resolution === Periods.CUSTOM || resolution === Periods.ALL_TIME) {
      resolution = Periods.DAYS;
    }

    let existingRequest = this.maybeGetRunningOrQueuedRequest(
      MetricTypes.SegmentMembershipMetric,
      Entities.SEGMENT,
      segmentId,
      null,
      resolution,
      range,
      null,
    );
    if (existingRequest !== null) {
      if (existingRequest.result) {
        return existingRequest.result;
      }
      return await existingRequest.request;
    }

    let options = buildRequestOptions(
      MetricTypes.SegmentMembershipMetric,
      Entities.SEGMENT,
      segmentId,
      null,
      resolution,
      range,
      null,
    );

    let apiRequest = this.api.request('metrics/segment_membership', options);
    let request = this.requestAndSerialize(
      MetricTypes.SegmentMembershipMetric,
      apiRequest,
      Entities.SEGMENT,
      segmentId,
      null,
      resolution,
      range,
      null,
      null,
      this.settings.environmentSettings.metricsTimezone,
      meta,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.SegmentMembershipMetric,
      Entities.SEGMENT,
      segmentId,
      null,
      resolution,
      range,
      null,
      request,
      meta,
    );

    this._requestStore = [...this._requestStore, metricRequest];

    return await metricRequest.runRequest();
  }

  lastAnalysisApiResult: AnalysisAPIResponseMetric[] | null = null;

  async fetchAnalysisMetrics(
    range: TimeRange,
    resolution: Periods,
    name: string,
    tagIds: string[],
    status: CampaignState[],
    dataTypes: string[],
    channels: string[],
    extraOptions?: {
      excludeZeros?: boolean;
      excludeNewslettersWithoutSends?: boolean;
      refreshData?: boolean;
    },
    subscriptionTopics?: SubscriptionTopicModel[],
  ) {
    let _extraOptions = merge({}, DEFAULT_ANALYSIS_EXTRA_OPTIONS, extraOptions);

    let { start, end } = range;

    let _range = {
      start: moment.tz(`${start!.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
      end: moment.tz(`${end!.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
    };

    let data = {
      start: _range.start.unix(),
      end: _range.end.unix(),
      res: resolution === Periods.MONTHS ? Periods.MONTHS : Periods.DAYS,
      name,
      tag_ids: tagIds,
      tz: this.metricsTz,
      version: 2,
      status,
      // The Analysis page uses "transactional" instead of "transactional_message".
      // One of the reasons being that URL query params in the Analysis page use
      // underscored for separating lists of items, so we can't use names with
      // underscores in that page's params
      data_type: dataTypes.map((t) => (t === 'transactional' ? 'transactional_message' : t)),
    };

    let beforeRequestTimestamp = unixNowMilliseconds();

    let results: AnalysisAPIResponseMetric[];

    if (!_extraOptions.refreshData && this.lastAnalysisApiResult) {
      results = this.lastAnalysisApiResult;
    } else {
      // The backend returns results for all data types if no data types are
      // provided, but in the UI we don't want that behavior. Instead we'll
      // shortcut to showing an empty state and not even query the API.
      if (dataTypes.length > 0) {
        results = (
          (await this.api.request('metrics/analysis', {
            // the "traditional" flag makes array query params be appended as
            // param=1&param=2 instead of param[]=1&param[]=2,
            // and the backend expects the former format
            traditional: true,
            data,
          })) as AnalysisAPIResponse
        ).metrics;
      } else {
        results = [];
      }

      this.lastAnalysisApiResult = results;
    }

    let queryTimeInMilliseconds = unixNowMilliseconds() - beforeRequestTimestamp;

    let preprocessedApiResults = results.map((r) => {
      if (r.type === 'newsletter') {
        return {
          ...r,
          _id: String(r.newsletter_id),
          id: `n_${r.newsletter_id}`,
          icon: 'newsletter',
        };
      } else if (r.type === 'transactional_message') {
        return {
          ...r,
          _id: String(r.transactional_id),
          id: `t_${r.transactional_id}`,
          icon: 'transactional-api',
          is_default: r.is_default ?? false,
        };
      } else {
        return {
          ...r,
          _id: String(r.campaign_id),
          id: String(r.campaign_id),
          icon: TYPE_ICONS[r.type],
        };
      }
    });

    let processResult = await this.workers.getWorker(CIOWorker.ProcessAnalysisMetrics).postMessage([
      preprocessedApiResults,
      channels,
      _range.end.diff(_range.start, 'days') + 1,
      _extraOptions.excludeZeros,
      _extraOptions.excludeNewslettersWithoutSends,
      dataTypes.includes('transactional'),
      {
        JOURNEY_METRICS_MEASURES,
        DELIVERY_METRICS_MEASURES,
        DELIVERY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.DeliveryMetrics),
        JOURNEY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.JourneyMetrics),
        METRICS_PER_ACTION_TYPE: deliveryStatesAndActionsPerType(true),
      },
      subscriptionTopics?.map((t) => ({ id: t.id, name: t.name })),
    ]);

    let finalResults = (processResult ?? []).map((d: any) => ({
      _id: d._id,
      id: d.id,
      name: d.name,
      type: d.type,
      icon: d.icon,
      is_default: d.is_default,
      channel: d.channel,
      conversion: d.conversion,
      data: {
        per_channel_metrics: d.per_channel_metrics,
        delivery_metrics: d.delivery_metrics,
        journey_metrics: d.journey_metrics,
        per_subscription_topic_metrics: d.per_subscription_topic_metrics,
      },
    }));

    return {
      data: finalResults,
      queryTime: queryTimeInMilliseconds,
    };
  }

  async fetchDashboardTestMetrics(
    entityId: string,
    range: TimeRange,
    resolution: Periods,
  ): Promise<DashboardTestResult> {
    let identifier = generateMetricIdentifier(
      MetricTypes.DashboardTests,
      Entities.ENVIRONMENT,
      entityId,
      Periods.DAYS,
      resolution,
      range,
      'email_action',
    );

    let existingRequest = this._requestStore.find((r) => r.id === identifier);

    if (existingRequest) {
      // If a request for this metric already exists but hasn't finished running
      // yet, return the same promise to avoid sending duplicate API requests.
      if (!existingRequest.result) {
        return await existingRequest.request;
      }

      // If the request for the same metric was performed in the past 30
      // seconds, return the same instance to reduce spamming the API.
      let now = Math.floor(Date.now() / 1000);
      if (existingRequest.fetchedAt && existingRequest.fetchedAt > now - 30) {
        return existingRequest.result as DashboardTestResult;
      }

      // If the existing request has a result and is older than 30 seconds,
      // remove it from the store and let the function continue to create a new
      // one.
      this._requestStore = this._requestStore.filter((r) => r.uuid !== existingRequest?.uuid);
    }

    let metricsTzRange = {
      start: moment.tz(`${range.start?.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
      end: moment.tz(`${range.end?.format('YYYY-MM-DD')} 00:00:00`, this.metricsTz),
    };

    let data = {
      start: metricsTzRange.start.unix(),
      end: metricsTzRange.end.unix(),
      res: resolution === Periods.MONTHS ? Periods.MONTHS : Periods.DAYS,
      version: 2,
      tz: this.metricsTz,
    };

    let meta = {
      environmentId: entityId,
    };

    let apiRequest = this.api.request('metrics/dashboard_tests', { data });
    let request = this.requestAndSerialize(
      MetricTypes.DashboardTests,
      apiRequest,
      Entities.ENVIRONMENT,
      entityId,
      Periods.DAYS,
      resolution,
      range,
      null,
      'email_action',
      this.settings.environmentSettings.metricsTimezone,
      meta,
    );

    let metricRequest = new MetricRequest(
      MetricTypes.DashboardTests,
      Entities.ENVIRONMENT,
      entityId,
      Periods.DAYS,
      resolution,
      range,
      'email_action',
      request,
      meta,
    );

    this._requestStore = [...this._requestStore, metricRequest];

    return await metricRequest.runRequest();
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    metrics: MetricsService;
  }
}
